import { useEffect, useState } from "react";
import { listRefundablePurchases, refundPurchase } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

const PACKAGE_LABEL = {
  pkg1: "1회 패키지",
  pkg4: "4회 패키지",
  pkg8: "8회 패키지",
  pkg12: "12회 패키지",
  once: "1회 분석 (legacy)",
};

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yy}-${mm}-${dd} ${hh}:${mi}`;
}

function shortPaymentRef(ref) {
  if (!ref) return "-";
  return ref.length > 16 ? ref.slice(0, 8) + "…" + ref.slice(-6) : ref;
}

export default function AdminRefunds({ onSignOut }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null); // selected purchase for modal
  const [reason, setReason] = useState("");
  const [forceMode, setForceMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {tone:'ok'|'err', msg}

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listRefundablePurchases();
      setRows(r);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openModal = (row) => {
    setTarget(row);
    setReason("");
    setForceMode(false);
    setFeedback(null);
  };
  const closeModal = () => {
    if (submitting) return;
    setTarget(null);
  };

  const submit = async () => {
    if (!target) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await refundPurchase({
        purchaseId: target.id,
        reason: reason.trim() || null,
        force: forceMode,
      });
      setFeedback({
        tone: "ok",
        msg: result.already
          ? "이미 환불된 결제예요 (DB만 동기화)."
          : `환불 완료 — ${(result.amount_won ?? target.amount_won ?? 0).toLocaleString()}원${
              result.forced ? ` (강제, ${result.credits_used}회 사용 무효화)` : ""
            }`,
      });
      await load();
      setTimeout(() => {
        setTarget(null);
        setFeedback(null);
      }, 1800);
    } catch (err) {
      if (err.requiresForce) {
        setFeedback({
          tone: "err",
          msg: `${err.message} 아래 "강제 환불"을 체크하면 진행 가능해요.`,
        });
        setForceMode(false);
      } else {
        setFeedback({ tone: "err", msg: err.message ?? "환불 실패" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin">
      <AdminSidebar active="refunds" onSignOut={onSignOut}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:18}}>
          <div>
            <h1 className="admin-h1">환불</h1>
            <p className="admin-sub">
              {loading
                ? "불러오는 중..."
                : `결제 ${rows.length}건 (paid + refunded)`}
            </p>
          </div>
          <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
            ⟳ 새로고침
          </button>
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            불러오지 못했어요: {error}
          </div>
        )}

        <div className="admin-table-wrap">
          <div style={{
            display:'grid',
            gridTemplateColumns:'160px 1fr 130px 110px 90px 130px 90px',
            gap:8,padding:'12px 18px',
            background:'#f7f8fa',borderBottom:'1px solid #eef0f4',
            fontSize:11,fontWeight:700,color:'#718096',textTransform:'uppercase',letterSpacing:.4,
          }}>
            <div>결제일</div>
            <div>사용자</div>
            <div>패키지</div>
            <div style={{textAlign:'right'}}>금액</div>
            <div style={{textAlign:'center'}}>사용/총</div>
            <div>상태</div>
            <div></div>
          </div>
          {loading ? (
            <div style={{padding:'40px 0',textAlign:'center'}}>
              <div className="spinner" style={{display:'inline-block'}}/>
            </div>
          ) : rows.length === 0 ? (
            <div style={{padding:'40px 0',textAlign:'center',color:'#718096',fontSize:13}}>
              환불 가능한 결제가 없어요.
            </div>
          ) : (
            rows.map((r) => {
              const isRefunded = r.status === "refunded";
              const used = r.credits_used ?? 0;
              const total = r.credits_total ?? "-";
              return (
                <div key={r.id} style={{
                  display:'grid',
                  gridTemplateColumns:'160px 1fr 130px 110px 90px 130px 90px',
                  gap:8,padding:'14px 18px',
                  borderBottom:'1px solid #f0f2f5',alignItems:'center',
                  fontSize:13,
                  background: isRefunded ? '#fafbfc' : '#fff',
                  opacity: isRefunded ? 0.7 : 1,
                }}>
                  <div style={{fontVariantNumeric:'tabular-nums',color:'#4a5568'}}>{formatDate(r.paid_at)}</div>
                  <div>
                    <div style={{fontWeight:700}}>{r.user?.display_name || r.user?.email || "(이름 없음)"}</div>
                    <div style={{fontSize:11,color:'#718096'}}>
                      {r.user?.email ?? "-"} · {shortPaymentRef(r.payment_ref)}
                    </div>
                  </div>
                  <div>{PACKAGE_LABEL[r.plan_type] ?? r.plan_type}</div>
                  <div style={{textAlign:'right',fontWeight:700,fontVariantNumeric:'tabular-nums'}}>
                    {(r.amount_won ?? 0).toLocaleString()}원
                  </div>
                  <div style={{textAlign:'center',fontVariantNumeric:'tabular-nums'}}>
                    {r.credits_total != null ? `${used} / ${total}` : "-"}
                  </div>
                  <div>
                    {isRefunded ? (
                      <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#9b89b322',color:'#5b497a'}}>
                        환불됨
                      </span>
                    ) : (
                      <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#27ae6022',color:'#1f7a3f'}}>
                        결제 완료
                      </span>
                    )}
                  </div>
                  <div style={{textAlign:'right'}}>
                    {!isRefunded && (
                      <button
                        onClick={() => openModal(r)}
                        style={{
                          padding:'6px 12px',borderRadius:8,fontSize:12,fontWeight:700,
                          background:'#fff',border:'1px solid #e74c3c66',color:'#c0392b',
                          cursor:'pointer',fontFamily:'inherit',
                        }}>
                        환불
                      </button>
                    )}
                    {isRefunded && r.refunded_at && (
                      <div style={{fontSize:11,color:'#718096'}} title={r.refund_reason ?? ''}>
                        {formatDate(r.refunded_at)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      {target && (
        <div
          onClick={closeModal}
          style={{
            position:'fixed',inset:0,background:'rgba(15,23,42,.4)',
            display:'flex',alignItems:'center',justifyContent:'center',zIndex:50,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background:'#fff',borderRadius:14,padding:24,width:480,maxWidth:'90vw',
              boxShadow:'0 20px 60px rgba(0,0,0,.2)',
            }}>
            <h2 style={{margin:'0 0 4px',fontSize:18}}>환불 처리</h2>
            <p style={{margin:'0 0 18px',fontSize:13,color:'#4a5568'}}>
              {target.user?.email ?? "사용자"} · {PACKAGE_LABEL[target.plan_type] ?? target.plan_type} ·{" "}
              <strong>{(target.amount_won ?? 0).toLocaleString()}원</strong>
            </p>

            <div style={{
              background:'#f7f8fa',padding:'10px 12px',borderRadius:10,
              fontSize:12,color:'#4a5568',marginBottom:14,
              fontVariantNumeric:'tabular-nums',
            }}>
              사용/총: {target.credits_used ?? 0} / {target.credits_total ?? "-"} ·
              결제처: {target.payment_provider ?? "toss"} ·
              결제키: {shortPaymentRef(target.payment_ref)}
            </div>

            <label style={{display:'block',marginBottom:14}}>
              <span style={{fontSize:12,fontWeight:700,color:'#4a5568'}}>환불 사유</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="예: 보호자 요청, 중복 결제, 영상 업로드 불가 등"
                rows={3}
                style={{
                  width:'100%',marginTop:6,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',resize:'vertical',
                }}
              />
            </label>

            {target.credits_used > 0 && (
              <label style={{display:'flex',gap:8,alignItems:'flex-start',marginBottom:14,
                padding:'10px 12px',background:'#fef9f0',borderRadius:10,
                border:'1px solid #ff993344',cursor:'pointer'}}>
                <input
                  type="checkbox"
                  checked={forceMode}
                  onChange={(e) => setForceMode(e.target.checked)}
                  style={{marginTop:3}}
                />
                <span style={{fontSize:12,color:'#a25d09',lineHeight:1.5}}>
                  <strong>강제 환불</strong> — {target.credits_used}회 이미 사용된 패키지를 환불합니다.
                  잔여 분석권이 0으로 회수돼요.
                </span>
              </label>
            )}

            {feedback && (
              <div style={{
                padding:'10px 12px',borderRadius:10,fontSize:12,marginBottom:14,
                background: feedback.tone === 'ok' ? '#27ae6018' : '#e74c3c18',
                color: feedback.tone === 'ok' ? '#1f7a3f' : '#c0392b',
              }}>
                {feedback.msg}
              </div>
            )}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button
                onClick={closeModal}
                disabled={submitting}
                style={{
                  padding:'10px 18px',borderRadius:10,
                  background:'#fff',border:'1px solid #e2e8f0',
                  fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
                }}>
                취소
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                style={{
                  padding:'10px 18px',borderRadius:10,
                  background:'#c0392b',border:'1px solid #c0392b',color:'#fff',
                  fontSize:13,fontWeight:700,
                  cursor: submitting ? 'wait' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  fontFamily:'inherit',
                }}>
                {submitting ? "처리 중..." : "환불 진행"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
